import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

// Per-(rootId, photoId, encryptedRating-tag) cache of decrypted ratings.
// The encryptedRating is the WCV1 packed string itself; if it changes (the
// owner re-rates a photo or rotation re-wraps the cipher under a new key)
// the cache misses naturally. This means we never have to listen for keyGen
// changes — the cipher tag *is* the version.
//
// Decrypt is fast (a single AES-GCM of ~6 bytes) but we still cache because
// the top-rated page re-decrypts on every navigation back to it, and the
// session-long batch can easily exceed a thousand photos in a busy root.

const DB_NAME = 'photo-vault-ratings';
const DB_VERSION = 1;
const STORE = 'photoRatings';

interface RatingEntry {
  // Composite key `${rootId}::${photoId}` — same convention as the dedup
  // store. IndexedDB doesn't compose primitives natively without a compound
  // key store, and a string is simpler across schema migrations.
  key: string;
  rootId: string;
  photoId: string;
  // First 32 chars of the encryptedRating ciphertext: serves as a cheap
  // version tag without bloating the row.
  cipherTag: string;
  rating: number;
  cachedAt: number;
}

interface RatingSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: RatingEntry;
    indexes: { byRoot: string };
  };
}

let dbPromise: Promise<IDBPDatabase<RatingSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<RatingSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<RatingSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const store = db.createObjectStore(STORE, { keyPath: 'key' });
          store.createIndex('byRoot', 'rootId');
        }
      },
    });
  }
  return dbPromise;
}

function compositeKey(rootId: string, photoId: string): string {
  return `${rootId}::${photoId}`;
}

function tagFor(encryptedRating: string): string {
  return encryptedRating.slice(0, 32);
}

export async function getCachedRating(
  rootId: string,
  photoId: string,
  encryptedRating: string,
): Promise<number | null> {
  try {
    const db = await getDb();
    const entry = await db.get(STORE, compositeKey(rootId, photoId));
    if (!entry) return null;
    if (entry.cipherTag !== tagFor(encryptedRating)) return null;
    return entry.rating;
  } catch {
    return null;
  }
}

// Bulk variant: hits the store once per call. Returns a Map keyed by photoId
// for O(1) lookup against the input list. Missing entries are simply absent.
export async function getCachedRatings(
  rootId: string,
  items: ReadonlyArray<{ photoId: string; encryptedRating: string }>,
): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  if (items.length === 0) return out;
  try {
    const db = await getDb();
    const tx = db.transaction(STORE, 'readonly');
    await Promise.all(
      items.map(async (it) => {
        const entry = await tx.store.get(compositeKey(rootId, it.photoId));
        if (!entry) return;
        if (entry.cipherTag !== tagFor(it.encryptedRating)) return;
        out.set(it.photoId, entry.rating);
      }),
    );
    await tx.done;
  } catch {
    // best-effort cache
  }
  return out;
}

export async function putCachedRatings(
  rootId: string,
  entries: ReadonlyArray<{ photoId: string; encryptedRating: string; rating: number }>,
): Promise<void> {
  if (entries.length === 0) return;
  try {
    const db = await getDb();
    const tx = db.transaction(STORE, 'readwrite');
    const now = Date.now();
    await Promise.all(
      entries.map((e) =>
        tx.store.put({
          key: compositeKey(rootId, e.photoId),
          rootId,
          photoId: e.photoId,
          cipherTag: tagFor(e.encryptedRating),
          rating: e.rating,
          cachedAt: now,
        }),
      ),
    );
    await tx.done;
  } catch {
    // best-effort cache
  }
}

// Manual escape hatch — not wired to UI yet. Useful when debugging cache
// drift.
export async function clearRootRatingCache(rootId: string): Promise<void> {
  try {
    const db = await getDb();
    const tx = db.transaction(STORE, 'readwrite');
    const index = tx.store.index('byRoot');
    let cursor = await index.openCursor(rootId);
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
  } catch {
    // ignore
  }
}
